import { CommonModule } from "@angular/common";
import { Component, Input } from "@angular/core";

import { IncidenciasIAService } from "../../incidencias-ia/incidencias-ia.service";
import { InventarioIAService } from "../../inventario-ia/inventario-ia.service";
import { MonitoreoIAService } from "../../inventario-ia/monitoreo-ia.service";
import { IncidenciaIA } from "../../shared/model/iso42001/incidencia-ia.model";
import {
  InventarioIA,
  InventarioIAHistorial,
} from "../../shared/model/iso42001/inventario-ia.model";
import {
  MatrizAccion,
  MatrizActivo,
  MatrizEvidencia,
  MatrizHistorial,
  MatrizVulnerabilidad,
  RiesgoProceso,
} from "../../shared/model/iso42001/matriz-riesgo.model";
import { MonitoreoIA } from "../../shared/model/iso42001/monitoreo-ia.model";
import { FechaBitacoraPipe } from "../fecha-bitacora.pipe";
import { ExpedienteBase } from "../expediente-comun/expediente-base";
import { fechaPdf } from "../expediente-comun/expediente-formato";
import { PdfExpediente } from "../expediente-comun/pdf-expediente";

type PestanaExp =
  | "resumen"
  | "inventario"
  | "riesgo"
  | "monitoreo"
  | "incidencias"
  | "procesos"
  | "evidencias"
  | "bitacora";

/**
 * Expediente del activo: la evidencia de trazabilidad para la certificación
 * ISO/IEC 42001, reunida en una sola ventana.
 *
 * El activo de la matriz llega del padre (ya viene con su árbol de amenazas y
 * sus cortes); lo demás —registro de inventario, monitoreos, incidencias,
 * riesgos de proceso y bitácoras— lo consulta este componente al abrir, cada
 * sección por su lado: si una falla, las otras se muestran igual.
 *
 * Todo es de SOLO LECTURA y usa únicamente endpoints que ya existen; no hay
 * backend nuevo. El nodo se cuelga de <body> (PantallaCompleta) porque la
 * pestaña de Material abre un contexto de apilamiento que taparía la ventana.
 */
@Component({
  selector: "app-expediente-activo-ia",
  standalone: true,
  templateUrl: "./expediente-activo-ia.component.html",
  styleUrls: ["./expediente-activo-ia.component.css"],
  imports: [CommonModule, FechaBitacoraPipe],
})
export class ExpedienteActivoIAComponent extends ExpedienteBase {
  @Input() activo!: MatrizActivo;

  pestana: PestanaExp = "resumen";
  /**
   * Las pestañas. La ayuda sale como tooltip: tiene que ir con el atributo
   * title nativo y NO con matTooltip, porque esta ventana se cuelga de <body>
   * muy por encima del overlay de Material y el tooltip quedaría escondido
   * detrás.
   */
  readonly secciones: { clave: PestanaExp; titulo: string; ayuda: string }[] = [
    { clave: "resumen", titulo: "Resumen", ayuda: "Todo el expediente en una vista: con qué se relaciona el activo y qué falta por cuadrar." },
    { clave: "inventario", titulo: "Ficha del inventario", ayuda: "Cómo está registrado este activo en el Inventario IA: para qué se usa, quién lo opera y qué impactos se le identificaron." },
    { clave: "riesgo", titulo: "Riesgo en la matriz", ayuda: "Su evaluación de riesgo: valoración del activo, amenazas, vulnerabilidades y el plan de acción para tratarlas." },
    { clave: "monitoreo", titulo: "Monitoreos", ayuda: "Evaluaciones periódicas del activo en el módulo Monitoreo IA. Son la prueba de que se le da vigilancia continua." },
    { clave: "incidencias", titulo: "Incidencias", ayuda: "Fallas, quejas o eventos que alguien reportó sobre este activo en el módulo de Incidencias IA." },
    { clave: "procesos", titulo: "Riesgos de proceso", ayuda: "Riesgos de la operación, no de un activo: por ejemplo, usar soluciones con IA sin evaluar su riesgo. Son los renglones del Plan de Gestión (folios RSK-DIA-001, RSK-DIA-002…); aquí salen los que mencionan a este activo." },
    { clave: "evidencias", titulo: "Evidencias", ayuda: "Los archivos adjuntos que respaldan lo capturado: imágenes, PDF, hojas de cálculo." },
    { clave: "bitacora", titulo: "Bitácoras", ayuda: "Quién cambió qué y cuándo, tanto en el inventario como en la matriz." },
  ];

  /**
   * "1 amenaza" / "2 amenazas", sin el "(s)" que hoy se lee en toda la
   * pantalla. El cero se resuelve solo: en español "0 amenazas" es plural.
   */
  plural(n: number, uno: string, varios: string): string {
    return n + " " + (n === 1 ? uno : varios);
  }

  /* ---------- Datos de cada sección ---------- */

  inventario: InventarioIA | null = null;
  cargandoInv = false;
  errorInv = "";
  /** El renglón trae un id de inventario que ya no existe. */
  vinculoRoto = false;

  monitoreos: MonitoreoIA[] = [];
  cargandoMon = false;
  errorMon = "";

  incidencias: IncidenciaIA[] = [];
  /** Total real en BD: la consulta trae a lo más 200 renglones. */
  totalIncidencias = 0;
  cargandoInc = false;
  errorInc = "";

  procesos: RiesgoProceso[] = [];
  cargandoProc = false;
  errorProc = "";
  /** Todos los riesgos de proceso; el filtro por folio se aplica aparte. */
  private riesgosProceso: RiesgoProceso[] = [];

  histInv: InventarioIAHistorial[] = [];
  cargandoHistInv = false;
  errorHistInv = "";

  histMatriz: MatrizHistorial[] = [];
  cargandoHistMat = false;
  errorHistMat = "";

  /** Plan de acción aplanado del árbol amenaza -> vulnerabilidad -> acción. */
  acciones: MatrizAccion[] = [];

  /** Evidencias adjuntas: las del activo y las de cada riesgo RSK que lo cita. */
  evidenciasActivo: MatrizEvidencia[] = [];
  /** "NO" cuando el activo declaró que las evidencias no aplican. */
  evidenciasAplicaActivo = "";
  evidenciasProcesos: { proceso: RiesgoProceso; lista: MatrizEvidencia[]; aplica?: string }[] = [];
  cargandoEvd = false;

  constructor(
    private readonly invServ: InventarioIAService,
    private readonly monServ: MonitoreoIAService,
    private readonly incServ: IncidenciasIAService,
  ) {
    super();
  }

  /* ---------- Carga: cada sección por su lado ---------- */

  protected cargar(): void {
    this.acciones = this.aplanarAcciones();
    const idInv = this.activo.idInventarioIa;
    const idAct = this.activo.idMatrizActivo;

    if (idInv) {
      this.cargandoInv = true;
      this.invServ.obtener(idInv).subscribe({
        next: (res) => {
          this.cargandoInv = false;
          if (!res || res.codigoError) {
            this.errorInv = (res && res.mensajeError) || "No se pudo consultar el inventario.";
          } else if (!res.inventario) {
            // El id capturado en la matriz ya no existe: vínculo roto, y se dice.
            this.vinculoRoto = true;
          } else {
            this.inventario = res.inventario;
            this.filtrarProcesos();
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.cargandoInv = false;
          this.errorInv = "No se pudo consultar el inventario.";
          this.cdr.markForCheck();
        },
      });

      this.cargandoMon = true;
      this.monServ.consultar(idInv).subscribe({
        next: (res) => {
          this.cargandoMon = false;
          if (!res || res.codigoError) {
            this.errorMon = (res && res.mensajeError) || "No se pudieron consultar los monitoreos.";
          } else {
            this.monitoreos = res.monitoreos || [];
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.cargandoMon = false;
          this.errorMon = "No se pudieron consultar los monitoreos.";
          this.cdr.markForCheck();
        },
      });

      this.cargandoInc = true;
      this.incServ.consultarKanban({ idInventarioIa: idInv }, 0, 200).subscribe({
        next: (res) => {
          this.cargandoInc = false;
          if (!res || res.codigoError) {
            this.errorInc = (res && res.mensajeError) || "No se pudieron consultar las incidencias.";
          } else {
            this.incidencias = res.incidencias || [];
            this.totalIncidencias = res.totalItems ?? this.incidencias.length;
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.cargandoInc = false;
          this.errorInc = "No se pudieron consultar las incidencias.";
          this.cdr.markForCheck();
        },
      });

      this.cargandoHistInv = true;
      this.invServ.historial(idInv).subscribe({
        next: (res) => {
          this.cargandoHistInv = false;
          if (!res || res.codigoError) {
            this.errorHistInv = (res && res.mensajeError) || "No se pudo consultar la bitácora del inventario.";
          } else {
            this.histInv = res.historial || [];
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.cargandoHistInv = false;
          this.errorHistInv = "No se pudo consultar la bitácora del inventario.";
          this.cdr.markForCheck();
        },
      });
    }

    if (idAct) {
      this.cargandoEvd = true;
      this.matrizServ.consultarEvidencias("ACTIVO", idAct).subscribe({
        next: (res) => {
          this.cargandoEvd = false;
          this.evidenciasActivo = (res && !res.codigoError && res.evidencias) || [];
          this.evidenciasAplicaActivo = (res && !res.codigoError && res.aplica) || "";
          this.cdr.markForCheck();
        },
        error: () => {
          this.cargandoEvd = false;
          this.cdr.markForCheck();
        },
      });

      this.cargandoHistMat = true;
      this.matrizServ.historial(idAct).subscribe({
        next: (res) => {
          this.cargandoHistMat = false;
          if (!res || res.codigoError) {
            this.errorHistMat = (res && res.mensajeError) || "No se pudo consultar la bitácora de la matriz.";
          } else {
            this.histMatriz = res.historial || [];
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.cargandoHistMat = false;
          this.errorHistMat = "No se pudo consultar la bitácora de la matriz.";
          this.cdr.markForCheck();
        },
      });
    }

    // Riesgos de proceso: el enlace es el folio IA- citado en texto libre
    // (activos_relacionados), así que se traen todos y se filtra aquí.
    this.cargandoProc = true;
    this.matrizServ.consultarRiesgos({ idMatriz: this.activo.idMatriz || 1 }).subscribe({
      next: (res) => {
        this.cargandoProc = false;
        if (!res || res.codigoError) {
          this.errorProc = (res && res.mensajeError) || "No se pudieron consultar los riesgos de proceso.";
        } else {
          this.riesgosProceso = res.riesgos || [];
          this.filtrarProcesos();
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.cargandoProc = false;
        this.errorProc = "No se pudieron consultar los riesgos de proceso.";
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * El folio puede venir del JOIN de la matriz o del propio registro de
   * inventario (el que llegue primero); con (?!\d) un IA-0001 no se confunde
   * con un hipotético IA-00010.
   */
  private filtrarProcesos(): void {
    const folio = this.folioInv;
    if (!folio) {
      this.procesos = [];
      return;
    }
    const escapado = folio.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const patron = new RegExp(escapado + "(?!\\d)");
    this.procesos = this.riesgosProceso.filter((r) => patron.test(r.activosRelacionados || ""));
    this.cargarEvidenciasProcesos();
  }

  /** Las evidencias de cada riesgo RSK relacionado (son pocos). */
  private cargarEvidenciasProcesos(): void {
    this.evidenciasProcesos = [];
    this.procesos.forEach((p) => {
      if (!p.idRiesgoProceso) {
        return;
      }
      const renglon: { proceso: RiesgoProceso; lista: MatrizEvidencia[]; aplica?: string } =
        { proceso: p, lista: [] };
      this.evidenciasProcesos.push(renglon);
      this.matrizServ.consultarEvidencias("RIESGO_PROCESO", p.idRiesgoProceso).subscribe({
        next: (res) => {
          renglon.lista = (res && !res.codigoError && res.evidencias) || [];
          renglon.aplica = (res && !res.codigoError && res.aplica) || "";
          this.cdr.markForCheck();
        },
        error: () => this.cdr.markForCheck(),
      });
    });
  }

  private aplanarAcciones(): MatrizAccion[] {
    const lista: MatrizAccion[] = [];
    (this.activo.amenazas || []).forEach((am) => {
      (am.vulnerabilidades || []).forEach((v) => {
        (v.acciones || []).forEach((ac) => lista.push(ac));
      });
    });
    return lista;
  }

  /* ---------- Presentación ---------- */

  get folioInv(): string {
    return this.activo.inventarioConsecutivo || (this.inventario && this.inventario.consecutivo) || "";
  }

  get ligado(): boolean {
    return !!this.activo.idInventarioIa;
  }

  elegir(p: PestanaExp): void {
    this.pestana = p;
  }

  /** El numerito que acompaña a cada pestaña. */
  conteoPestana(clave: PestanaExp): string {
    switch (clave) {
      // La ficha del inventario es una sola: un globito con "1" no informa nada
      // y ensucia la barra. Sin texto, el *ngIf de la plantilla no lo pinta.
      case "inventario": return "";
      case "riesgo": return String((this.activo.amenazas || []).length);
      case "monitoreo": return String(this.monitoreos.length);
      case "incidencias": return String(this.totalIncidencias || this.incidencias.length);
      case "procesos": return String(this.procesos.length);
      case "evidencias": return String(this.totalEvidencias);
      case "bitacora": return String(this.histInv.length + this.histMatriz.length);
      default: return "";
    }
  }

  get vulnerabilidadesTotal(): number {
    let n = 0;
    (this.activo.amenazas || []).forEach((am) => {
      n += (am.vulnerabilidades || []).length;
    });
    return n;
  }

  get accionesPorAplicar(): number {
    // Igual que el libro: cuenta todo lo que no esté "Aplicada" (incluye
    // acciones sin estado), para que ambos números siempre cuadren.
    return this.acciones.filter((a) => a.estado !== "Aplicada").length;
  }

  get incidenciasAbiertas(): number {
    return this.incidencias.filter(
      (i) => i.estadoEvento === "ABIERTO" || i.estadoEvento === "PROCESO",
    ).length;
  }

  get ultimoMonitoreo(): MonitoreoIA | null {
    // El backend las regresa de la más reciente a la más antigua.
    return this.monitoreos.length ? this.monitoreos[0] : null;
  }

  /** Mismo mapa que usa la matriz completa, para que los colores cuadren. */
  claseRiesgo(clasificacion?: string): string {
    switch (clasificacion) {
      case "Muy Alto": return "r-muyalto";
      case "Alto": return "r-alto";
      case "Medio": return "r-medio";
      case "Bajo": return "r-bajo";
      default: return "";
    }
  }

  /** Las bandas de severidad de los riesgos de proceso usan otros nombres. */
  claseBanda(clasificacion?: string): string {
    switch (clasificacion) {
      case "Crítico":
      case "Critico":
      case "Muy Alto": return "r-muyalto";
      case "Alta":
      case "Alto": return "r-alto";
      case "Media":
      case "Medio": return "r-medio";
      case "Baja":
      case "Bajo": return "r-bajo";
      default: return "";
    }
  }

  etiquetaDictamen(d?: string): string {
    switch (d) {
      case "FAVORABLE": return "Favorable";
      case "FAVORABLE_OBS": return "Favorable con observaciones";
      case "NO_FAVORABLE": return "No favorable";
      default: return d || "—";
    }
  }

  claseDictamen(d?: string): string {
    switch (d) {
      case "FAVORABLE": return "d-ok";
      case "FAVORABLE_OBS": return "d-obs";
      case "NO_FAVORABLE": return "d-mal";
      default: return "";
    }
  }

  etiquetaConclusion(c?: string | null): string {
    switch (c) {
      case "SIN_CAMBIOS": return "Sin cambios";
      case "MEJORA": return "Mejora";
      case "REEVALUAR_RIESGOS": return "Reevaluar riesgos";
      case "REGRESA_DESARROLLO": return "Regresa a desarrollo";
      case "RETIRO": return "Retiro";
      default: return c || "—";
    }
  }

  etiquetaEstadoInc(e?: string): string {
    switch (e) {
      case "ABIERTO": return "Abierta";
      case "PROCESO": return "En proceso";
      case "ATENDIDO": return "Atendida";
      case "VOBO": return "Con VoBo";
      default: return e || "—";
    }
  }

  claseEstadoInc(e?: string): string {
    switch (e) {
      case "ABIERTO": return "e-abierta";
      case "PROCESO": return "e-proceso";
      case "ATENDIDO": return "e-atendida";
      case "VOBO": return "e-vobo";
      default: return "";
    }
  }

  claseAccion(estado?: string): string {
    // El catálogo del modal captura "Programada" y "Aplicada".
    switch (estado) {
      case "Aplicada": return "a-ok";
      case "Programada": return "a-prog";
      default: return "";
    }
  }

  /* ---------- Evidencias adjuntas ---------- */

  get totalEvidencias(): number {
    let n = this.evidenciasActivo.length;
    this.evidenciasProcesos.forEach((p) => {
      n += p.lista.length;
    });
    return n;
  }

  /* ---------- Bitácoras legibles ---------- */

  /**
   * Las bitácoras guardan el nombre físico de la columna; sin esta traducción
   * el usuario ve "fecha_alta" o "id_ciclo_vida". Reúne las etiquetas del
   * historial del inventario y las de la matriz; un campo no listado se
   * muestra con espacios en lugar de guiones bajos, nunca crudo.
   */
  private static readonly ETIQUETAS_CAMPO: { [campo: string]: string } = {
    // Inventario IA
    nombre_ia: "Nombre del sistema de IA",
    version: "Versión",
    proposito: "Propósito",
    uso_previsto: "Uso previsto",
    id_area: "Área propietaria",
    propietario_externo: "Propietario externo",
    id_ciclo_vida: "Etapa del ciclo de vida",
    fecha_alta: "Fecha de alta",
    fecha_baja: "Fecha de baja",
    ubicacion: "Ubicación",
    componentes_sistema: "Componentes del sistema",
    componentes_datos: "Componentes de datos",
    herramientas_ia: "Herramientas de IA",
    sistemas_informaticos: "Sistemas informáticos",
    recursos_humanos: "Recursos humanos",
    procesos_intervencion: "Procesos en los que interviene",
    id_nivel_supervision: "Nivel de supervisión humana",
    areas_usuarias: "Áreas usuarias",
    impactos: "Impactos",
    // Matriz de riesgos
    nombre_activo: "Nombre del activo",
    tipo_activo: "Tipo de activo",
    val_confidencialidad: "Confidencialidad",
    val_integridad: "Integridad",
    val_disponibilidad: "Disponibilidad",
    justificacion_valoracion: "Justificación de la valoración",
    activos_dependientes: "Activos dependientes",
    id_inventario_ia: "Folio del inventario IA",
    fecha_identificacion: "Fecha de identificación",
    amenazas: "Amenazas",
    seguimiento: "Seguimiento",
    alta: "Alta del activo",
    // Ya no se genera; queda para las bases sin la limpieza de la bitácora.
    migracion: "Origen del registro",
    // Comunes
    estatus: "Estatus",
  };

  protected readonly etiquetasCampo = ExpedienteActivoIAComponent.ETIQUETAS_CAMPO;

  /* ---------- Resumen en prosa: el "match" dicho con palabras ---------- */

  /**
   * La frase que un auditor (o cualquiera) puede leer de corrido. Se arma con
   * los mismos datos de las tarjetas y se va completando conforme llegan.
   */
  resumenLegible(): string {
    const nombre = this.activo.nombreActivo || "El activo";
    const partes: string[] = [];

    if (this.inventario) {
      partes.push(`${nombre} está registrado en el Inventario IA con el folio ${this.folioInv}`);
    } else if (this.vinculoRoto) {
      partes.push(`${nombre} apunta a un folio de inventario que ya no existe`);
    } else if (this.ligado) {
      partes.push(`${nombre} está ligado al Inventario IA`);
    } else {
      partes.push(`${nombre} todavía no está ligado al Inventario IA`);
    }

    const nAm = (this.activo.amenazas || []).length;
    let matriz = `su riesgo se evalúa en la matriz como ${this.activo.consecutivo || "—"}, con ${this.plural(nAm, "amenaza", "amenazas")} y ${this.plural(this.acciones.length, "acción de tratamiento", "acciones de tratamiento")}`;
    if (this.accionesPorAplicar > 0) {
      matriz += ` (${this.accionesPorAplicar} todavía por aplicar)`;
    }
    partes.push(matriz);

    if (this.monitoreos.length) {
      const u = this.ultimoMonitoreo;
      partes.push(`se le han hecho ${this.plural(this.monitoreos.length, "evaluación de monitoreo", "evaluaciones de monitoreo")} y la más reciente salió ${this.etiquetaDictamen(u ? u.dictamen : undefined).toLowerCase()}`);
    } else if (this.ligado) {
      partes.push("todavía no tiene evaluaciones de monitoreo");
    }

    if (this.incidencias.length) {
      partes.push(`tiene ${this.plural(this.totalIncidencias || this.incidencias.length, "incidencia reportada", "incidencias reportadas")}, ${this.incidenciasAbiertas} sin cerrar`);
    } else if (this.ligado) {
      partes.push("no tiene incidencias reportadas");
    }

    if (this.procesos.length) {
      partes.push(`y ${this.plural(this.procesos.length, "riesgo de proceso del Plan de Gestión lo menciona", "riesgos de proceso del Plan de Gestión lo mencionan")}`);
    } else if (this.folioInv) {
      partes.push("y ningún riesgo de proceso del Plan de Gestión lo menciona");
    }

    return partes.join("; ") + ".";
  }

  /* ---------- Verificación de consistencia ---------- */

  /**
   * Los cruces que deben cuadrar entre módulos, dichos uno por uno. Si alguien
   * modificó algo y una relación se rompió o quedó desalineada, aquí se ve en
   * rojo en lugar de pasar desapercibido. Sólo se listan las comprobaciones
   * que ya se pueden hacer (las que dependen del inventario esperan a que
   * termine su consulta).
   */
  verificaciones(): { ok: boolean; texto: string }[] {
    const lista: { ok: boolean; texto: string }[] = [];

    if (!this.ligado) {
      lista.push({ ok: false, texto: "Al activo le falta capturar su folio del Inventario IA." });
    } else if (this.vinculoRoto) {
      lista.push({ ok: false, texto: "El folio del inventario capturado ya no existe: apunta a un activo borrado o renumerado." });
    } else if (this.inventario) {
      lista.push({ ok: true, texto: `Vínculo válido con el inventario (${this.folioInv}).` });

      const nomMatriz = (this.activo.nombreActivo || "").trim();
      const nomInv = (this.inventario.nombreIa || "").trim();
      if (nomMatriz && nomInv && nomMatriz !== nomInv) {
        lista.push({
          ok: false,
          texto: `El nombre en la matriz ("${nomMatriz}") no coincide con el del inventario ("${nomInv}"): alguien renombró uno de los dos.`,
        });
      } else {
        lista.push({ ok: true, texto: "El nombre coincide entre la matriz y el inventario." });
      }

      if (this.inventario.estatus !== "ACTIVO") {
        lista.push({ ok: false, texto: "El activo está dado de baja en el inventario, pero su riesgo sigue en la matriz." });
      } else {
        lista.push({ ok: true, texto: "El activo está vigente (ACTIVO) en el inventario." });
      }
    }

    if (this.ligado && !this.cargandoMon && !this.errorMon) {
      lista.push(this.monitoreos.length
        ? { ok: true, texto: `Tiene evidencia de monitoreo continuo (${this.plural(this.monitoreos.length, "evaluación", "evaluaciones")}).` }
        : { ok: false, texto: "No tiene ninguna evaluación de monitoreo continuo." });
    }

    if ((this.activo.amenazas || []).length > 0) {
      lista.push(this.acciones.length
        ? { ok: true, texto: "Toda amenaza identificada tiene plan de acción capturado." }
        : { ok: false, texto: "Hay amenazas identificadas pero ninguna acción de tratamiento capturada." });
    }

    return lista;
  }

  /* ---------- Exportar PDF: el expediente imprimible ---------- */

  /**
   * Documento real por secciones (texto, no captura de pantalla), con el mismo
   * armado que el PDF de denuncias pero en el azul del formato FOS-005.
   */
  protected generarPdf(): void {
    const a = this.activo;
    const doc = new PdfExpediente([243, 246, 250]);
    doc.membrete();
    // Sin flechas Unicode: la fuente estándar del PDF no las tiene y
    // descomponía la línea entera. Dos renglones centrados y cortos.
    doc.portada({
      titulo: "Expediente del activo de IA",
      rubro: "MATRIZ DE RIESGOS · INVENTARIO",
      folio: `${a.consecutivo || "—"}  ·  ${this.folioInv || "sin folio IA"}`,
      saltoFolio: 7,
      nombre: a.nombreActivo || "—",
      riesgo: `Riesgo actual: ${a.riesgoActual ?? "—"} (${a.clasificacionActual || "—"})   ·   riesgo residual: ${a.riesgoResidual ?? "—"} (${a.clasificacionResidual || "—"})`,
    });
    // La misma lectura en prosa que abre el resumen en pantalla, y los cruces
    // que deben cuadrar con su semáforo.
    doc.resumen(this.resumenLegible(), this.verificaciones());
    this.pdfInventario(doc);
    this.pdfValoracion(doc);
    this.pdfAmenazas(doc);
    this.pdfCortes(doc);
    this.pdfMonitoreo(doc);
    this.pdfIncidencias(doc);
    this.pdfProcesos(doc);
    this.pdfEvidencias(doc);
    doc.terminar(a.consecutivo || "", `Expediente-${a.consecutivo || "activo"}${this.folioInv ? "-" + this.folioInv : ""}.pdf`);
  }

  /** Identificación (inventario). */
  private pdfInventario(doc: PdfExpediente): void {
    const inv = this.inventario;
    doc.titulo("Ficha del inventario");
    if (inv) {
      doc.fila("Folio", inv.consecutivo || "—");
      doc.fila("Nombre / versión", `${inv.nombreIa || "—"}  ${inv.version || ""}`.trim());
      doc.fila("Propósito", inv.proposito || "—");
      doc.fila("Uso previsto", inv.usoPrevisto || "—");
      doc.fila("Área propietaria", inv.areaNombre || "—");
      doc.fila("Áreas usuarias", inv.areasUsuariasNombres || "—");
      doc.fila("Ciclo de vida", inv.cicloVidaNombre || "—");
      doc.fila("Nivel de supervisión", inv.nivelSupervisionNombre || "—");
      doc.fila("Ubicación", inv.ubicacion || "—");
      doc.fila("Fecha de alta", fechaPdf(inv.fechaAlta));
      doc.fila("Estatus", inv.estatus || "—");
    } else {
      doc.parrafo(this.sinFichaInventario());
    }
    doc.y += 2;
  }

  /** Por qué el PDF no trae la ficha del inventario. */
  private sinFichaInventario(): string {
    if (this.vinculoRoto) {
      return "El folio capturado en la matriz ya no existe en el inventario: apunta a un activo borrado o renumerado.";
    }
    return this.ligado
      ? "No fue posible consultar el registro del inventario."
      : "Este riesgo no está ligado a ningún activo del Inventario IA.";
  }

  private pdfValoracion(doc: PdfExpediente): void {
    const a = this.activo;
    doc.titulo("Valoración del riesgo (Matriz FOS-005)");
    doc.fila("Valoración C / I / D",
      `${a.valConfidencialidad ?? "—"} / ${a.valIntegridad ?? "—"} / ${a.valDisponibilidad ?? "—"}   (valor del activo: ${a.valorActivo ?? "—"})`);
    doc.fila("Riesgo actual", `${a.riesgoActual ?? "—"} — ${a.clasificacionActual || "—"}`);
    doc.fila("Riesgo residual", `${a.riesgoResidual ?? "—"} — ${a.clasificacionResidual || "—"}`);
    doc.fila("Comprobación máxima", a.periodicidadMaxima || "—");
    doc.fila("Fecha de identificación", fechaPdf(a.fechaIdentificacion));
    if (a.justificacionValoracion) { doc.fila("Justificación", a.justificacionValoracion); }
    if (a.activosDependientes) { doc.fila("Activos dependientes", a.activosDependientes); }
    doc.y += 2;
  }

  /** Amenazas y plan. */
  private pdfAmenazas(doc: PdfExpediente): void {
    doc.titulo("Amenazas, vulnerabilidades y plan de acción");
    const amenazas = this.activo.amenazas || [];
    if (!amenazas.length) { doc.parrafo("Sin amenazas capturadas."); }
    amenazas.forEach((am) => {
      doc.asegura(12);
      doc.pdf.setFont("helvetica", "bold");
      doc.pdf.setFontSize(10);
      doc.tinta();
      doc.pdf.text(`• ${am.nombreAmenaza || "—"}  (probabilidad: ${am.probActual ?? "—"}, tras el plan: ${am.probTrasPlan ?? "—"})`, doc.margenIzq + 2, doc.y + 3.7);
      doc.y += 6.4;
      (am.vulnerabilidades || []).forEach((v) => this.pdfVulnerabilidad(doc, v));
      doc.y += 1.5;
    });
    doc.y += 2;
  }

  private pdfVulnerabilidad(doc: PdfExpediente, v: MatrizVulnerabilidad): void {
    doc.pdf.setFont("helvetica", "normal");
    doc.pdf.setFontSize(9.5);
    doc.tinta();
    doc.sangrado(`Vulnerabilidad: ${v.nombreVulnerabilidad || "—"}`, 7, 12);
    (v.acciones || []).forEach((ac) => {
      const texto = `Acción [${ac.estado || "—"}${ac.prioridad ? ", prioridad " + ac.prioridad : ""}] ${ac.descripcion || "—"}`
        + (ac.responsable ? ` — responsable: ${ac.responsable}` : "")
        + (ac.fechaEjecucion ? ` — fecha: ${fechaPdf(ac.fechaEjecucion)}` : "");
      doc.sangrado(texto, 11, 16);
    });
    doc.y += 1.5;
  }

  /** Cortes de la matriz. */
  private pdfCortes(doc: PdfExpediente): void {
    doc.titulo("Seguimientos del plan de acción");
    const cortes = this.activo.seguimientos || [];
    if (!cortes.length) { doc.parrafo("Sin cortes registrados."); }
    cortes.forEach((s) => doc.fila(fechaPdf(s.fechaMonitoreo), s.resultado || "—"));
    doc.y += 2;
  }

  /** Monitoreo continuo. */
  private pdfMonitoreo(doc: PdfExpediente): void {
    doc.titulo("Monitoreo continuo del activo");
    if (!this.monitoreos.length) {
      doc.parrafo(this.ligado ? "Sin evaluaciones de monitoreo." : "No aplica: el riesgo no está ligado al inventario.");
    }
    this.monitoreos.forEach((m) => {
      doc.fila(`${m.consecutivo || "—"} · ${fechaPdf(m.fechaEvaluacion)}`,
        `${this.etiquetaDictamen(m.dictamen)} — conclusión: ${this.etiquetaConclusion(m.conclusion)}`);
    });
    doc.y += 2;
  }

  private pdfIncidencias(doc: PdfExpediente): void {
    doc.titulo("Incidencias del activo");
    if (!this.incidencias.length) {
      doc.parrafo(this.ligado ? "Sin incidencias registradas." : "No aplica: el riesgo no está ligado al inventario.");
    }
    this.incidencias.forEach((i) => {
      doc.fila(`#${i.idIncidenciaIa} · ${fechaPdf(i.fechaApertura)}`,
        `${this.etiquetaEstadoInc(i.estadoEvento)}${i.gravedad ? " — gravedad " + i.gravedad : ""}${i.tipoEvento ? " — " + i.tipoEvento : ""}: ${i.descripcionEvento || "—"}`);
    });
    if (this.incidencias.length < this.totalIncidencias) {
      doc.parrafo(`Se listan las ${this.incidencias.length} más recientes de ${this.plural(this.totalIncidencias, "incidencia registrada", "incidencias registradas")}.`);
    }
    doc.y += 2;
  }

  private pdfProcesos(doc: PdfExpediente): void {
    doc.titulo("Riesgos de proceso que mencionan este activo");
    // En pantalla esto se explica al pasar el mouse; el PDF no tiene tooltips,
    // asi que la aclaracion va escrita o el lector se queda sin saber que son.
    doc.parrafo("Riesgos de la operación, no de un activo: por ejemplo, usar soluciones con IA sin evaluar su riesgo. Son los renglones del Plan de Gestión y aquí se listan los que mencionan a este activo.");
    if (!this.procesos.length) { doc.parrafo("Ningún riesgo de proceso menciona este activo."); }
    this.procesos.forEach((r) => {
      doc.fila(`${r.consecutivo || "—"}`,
        `${r.descripcion || "—"} — severidad ${r.severidadInicial ?? "—"} (${r.clasificacionInicial || "—"}), reevaluada ${r.severidadReevaluada ?? "—"} (${r.clasificacionReevaluada || "—"})${r.estrategia ? " — estrategia: " + r.estrategia : ""}`);
    });
  }

  /** Evidencias adjuntas (del activo y de los riesgos RSK relacionados). */
  private pdfEvidencias(doc: PdfExpediente): void {
    doc.titulo("Evidencias adjuntas");
    if (this.evidenciasAplicaActivo === "NO") {
      doc.parrafo("El activo declaró que las evidencias NO APLICAN: estado justificado, no un faltante.");
    } else if (!this.totalEvidencias) {
      doc.parrafo("Sin evidencias adjuntas.");
    }
    this.evidenciasActivo.forEach((e) => doc.evidencia(e, " — del activo en la matriz"));
    this.evidenciasProcesos.forEach((p) => {
      p.lista.forEach((e) => doc.evidencia(e, ` — del riesgo ${p.proceso.consecutivo || "de proceso"}`));
    });
  }
}
